import { ISbStoryData } from 'storyblok-js-client';

import {
  CardProps,
  ArticleStoryblok,
  CaseCategoryStoryblok,
  CustomerGoalStoryblok,
  Locales,
  DataSourceEntry,
} from '@monoimin/sb-types';

// eslint-disable-next-line @nx/enforce-module-boundaries, import/no-cycle
import { articleIntroDict, replaceTemplate } from '@monoimin/intl';

import { getArticleUrl } from '../articles';
import { getImageSrc } from '../images';
import { formatRichTextToString, getCustomFormat } from '../formatters';
import { ArticlePageType, getDatasourceByValue } from '../helpers';

import { createPropsForImage } from './createPropsForImage';

type CreatePropsForArticleCardArgs = {
  blok: ISbStoryData<ArticleStoryblok>;
  categories?: Array<DataSourceEntry>;
  caseCategories?: ISbStoryData<CaseCategoryStoryblok>[];
  customerGoals?: ISbStoryData<CustomerGoalStoryblok>[];
  locale: Locales;
  isCaseCategoryPage?: boolean;
  readingTime?: number | string | null;
  pageType?: ArticlePageType;
  captionPosition?: 'top' | 'bottom';
  openLinksInNewWindow?: boolean;
};

const getCaptionForArticleCard = (args: CreatePropsForArticleCardArgs) => {
  const {
    blok,
    caseCategories,
    customerGoals,
    categories,
    isCaseCategoryPage,
    pageType,
  } = args;

  const { content } = blok;

  const { articleType, caseCategory, customerGoal, category } = content;
  if (articleType === 'case') {
    const categoryName = caseCategories?.find((c) => c.uuid === caseCategory)
      ?.content?.name;

    const goalName = customerGoals?.find((c) => c.uuid === customerGoal)
      ?.content?.shortName;

    return isCaseCategoryPage ? goalName : categoryName;
  }

  return getDatasourceByValue(categories, category, pageType)?.dimension_value;
};

const getIconCaptionForArticleCard = (args: CreatePropsForArticleCardArgs) => {
  const { blok, locale } = args;

  const { content } = blok;

  const { articleType, currency, budget } = content;

  if (articleType === 'case' && budget) {
    const toCurrency = new Intl.NumberFormat(locale, {
      currency: currency,
      style: 'currency',
      minimumFractionDigits: 0,
      useGrouping: true,
    });

    return toCurrency.format(budget);
  }

  return '';
};
const getArticleDetails = (
  args: CreatePropsForArticleCardArgs,
  articleType: 'case' | 'article' | 'insider'
) => {
  const { blok, readingTime, locale } = args;
  const { content } = blok;
  if (!readingTime) {
    return {};
  }

  const authorName =
    typeof content.expert !== 'string' && content.expert?.content?.name;
  const authorImageUrl =
    typeof content.expert !== 'string' &&
    getImageSrc({
      image: content.expert?.content?.photo,
    });

  const format = getCustomFormat(locale);
  const timeToReadShortText = replaceTemplate(
    articleIntroDict[locale].timeToReadShort,
    {
      timeToRead: String(readingTime),
    }
  );

  return {
    author: { name: authorName || '', image: authorImageUrl || '' },
    readingTime: timeToReadShortText || '',
    date: format(
      articleType === 'insider'
        ? blok.first_published_at || ''
        : blok.published_at || ''
    ),
  };
};

export const createPropsForArticleCard = (
  args: CreatePropsForArticleCardArgs
): CardProps => {
  const { blok, locale, captionPosition, openLinksInNewWindow } = args;

  const { content } = blok;

  const { lead, image, imageNew, heading, articleType } = content;

  const imageProps = createPropsForImage(imageNew, image);

  const caption = getCaptionForArticleCard({
    ...args,
    pageType: articleType === 'article' ? 'imin-blog' : 'imin-insider',
  });
  const captionProps = {
    top: {
      overtitle: caption,
    },
    bottom: {
      caption: caption,
    },
  };
  return {
    url: getArticleUrl(blok, locale),
    imageUrl: imageProps ? imageProps.src : '',
    title: heading,
    description: formatRichTextToString(lead),
    ...captionProps[captionPosition || 'bottom'],
    iconCaption: getIconCaptionForArticleCard(args),
    icon: articleType === 'case' ? 'investment' : undefined,
    prefetch: false,
    target: openLinksInNewWindow ? '_blank' : undefined,
    ...getArticleDetails(args, articleType),
  };
};

export default createPropsForArticleCard;
